import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, FileText, Trash2, Download } from 'lucide-react';

const ContratsList = () => {
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContrats();
  }, []);

  const loadContrats = async () => {
    try {
      const res = await axios.get(`${API}/contrats`, { withCredentials: true });
      setContrats(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatut = async (contratId, newStatut) => {
    try {
      await axios.patch(`${API}/contrats/${contratId}/statut`, { statut: newStatut }, { withCredentials: true });
      toast.success('Statut mis à jour');
      loadContrats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async (contratId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) return;
    try {
      await axios.delete(`${API}/contrats/${contratId}`, { withCredentials: true });
      toast.success('Contrat supprimé');
      loadContrats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadContrat = async (contrat) => {
    try {
      const response = await axios.get(`${API}/contrats/${contrat.id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename from contrat data
      const eventDate = contrat.event_date ? new Date(contrat.event_date).toISOString().split('T')[0] : '';
      const eventType = contrat.event_type ? contrat.event_type.replace('_', ' ') : 'Contrat';
      link.download = `CONTRAT ${eventType} - ${contrat.client_nom || 'Client'} - ${eventDate}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading contrat PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-stone-800">Contrats</h1>
        <p className="text-sm sm:text-base text-stone-600">Gérez vos contrats</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-stone-700">N° Contrat</TableHead>
                <TableHead className="text-stone-700">Client</TableHead>
                <TableHead className="text-stone-700">Date Événement</TableHead>
                <TableHead className="text-stone-700">Type</TableHead>
                <TableHead className="text-stone-700">Statut</TableHead>
                <TableHead className="text-right text-stone-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Chargement...</TableCell></TableRow>
              ) : contrats.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-stone-500">Aucun contrat. Créez-en un depuis un devis.</TableCell></TableRow>
              ) : (
                contrats.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.numero}</TableCell>
                    <TableCell>{c.client_nom || '-'}</TableCell>
                    <TableCell>
                      {c.date_evenement_principal 
                        ? new Date(c.date_evenement_principal).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <span className="capitalize">{c.event_type?.replace('_', ' ') || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <select
                        value={c.statut || 'brouillon'}
                        onChange={(e) => handleChangeStatut(c.id, e.target.value)}
                        className="text-sm px-2 py-1 rounded border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="brouillon">📄 Brouillon</option>
                        <option value="signe">✍️ Signé</option>
                      </select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" asChild title="Voir le devis associé">
                          <Link to={`/devis/${c.devis_id}`} target="_blank">
                            <FileText className="h-4 w-4 text-stone-500" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="sm" asChild title="Voir le contrat">
                          <Link to={`/contrat/${c.id}`} target="_blank">
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDownloadContrat(c)}
                          title="Télécharger le contrat (PDF)"
                          className="text-amber-600 hover:text-amber-700"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(c.id)}
                          title="Supprimer le contrat"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContratsList;
